import React, { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Modal, Button, Spinner } from "react-bootstrap";
import {
  enviarSolicitacao,
  postSolicitacaoAdequacao,
} from "../../../../../../services";
import type { DadosSindicatoDetalhe } from "../../../../../../services";
import type { SecaoDocumento } from "../../../../../../domain/shipping/models";
import NotificationToast from "../../../../../../components/NotificationToast/NotificationToast";
import ComprovanteEnvioDocumentacaoV2Corpo from "../ComprovanteEnvioDocumentacaoV2Corpo/ComprovanteEnvioDocumentacaoV2Corpo";
import { gerarPdfBlobComprovanteEnvioV2 } from "../../utils/comprovanteEnvioV2Pdf";
import { blocosResumoDasSecoes } from "../../utils/resumoDocumentacaoEnvioV2";
import "./ConfirmacaoEnvioV2Modal.css";
import "components/PageLoading/PageLoading.css";

export type ModoEnvioDocumentacaoV2 = "documentacao" | "adequacao";

export type ConfirmacaoEnvioV2ModalProps = {
  show: boolean;
  onHide: () => void;
  solicitacaoId: number | null;
  dadosSindicato: DadosSindicatoDetalhe | null;
  secoesObrigatorios: SecaoDocumento[];
  secoesBonus: SecaoDocumento[];
  secoesBonusExtra: SecaoDocumento[];
  modoEnvio?: ModoEnvioDocumentacaoV2;
  onEnvioSuccess?: () => void;
};

const ConfirmacaoEnvioV2Modal: React.FC<ConfirmacaoEnvioV2ModalProps> = ({
  show,
  onHide,
  solicitacaoId,
  dadosSindicato,
  secoesObrigatorios,
  secoesBonus,
  secoesBonusExtra,
  modoEnvio = "documentacao",
  onEnvioSuccess,
}) => {
  const isAdequacao = modoEnvio === "adequacao";
  const pdfSourceRef = useRef<HTMLDivElement | null>(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({ show: false, message: "", type: "success" });

  const secoesObrigatoriosResumo = useMemo(
    () =>
      isAdequacao
        ? secoesObrigatorios.filter(
            (s) =>
              s.canEditPergunta === true ||
              s.canEditUpload === true ||
              s.canEditObservacao === true,
          )
        : secoesObrigatorios,
    [isAdequacao, secoesObrigatorios],
  );
  const secoesBonusResumo = useMemo(
    () =>
      isAdequacao
        ? secoesBonus.filter(
            (s) =>
              s.canEditPergunta === true ||
              s.canEditUpload === true ||
              s.canEditObservacao === true,
          )
        : secoesBonus,
    [isAdequacao, secoesBonus],
  );
  const secoesBonusExtraResumo = useMemo(
    () =>
      isAdequacao
        ? secoesBonusExtra.filter(
            (s) =>
              s.canEditPergunta === true ||
              s.canEditUpload === true ||
              s.canEditObservacao === true,
          )
        : secoesBonusExtra,
    [isAdequacao, secoesBonusExtra],
  );

  const blocos = useMemo(
    () =>
      blocosResumoDasSecoes(
        secoesObrigatoriosResumo,
        secoesBonusResumo,
        secoesBonusExtraResumo,
      ),
    [secoesObrigatoriosResumo, secoesBonusResumo, secoesBonusExtraResumo],
  );

  const tituloLinha2Comprovante = isAdequacao ? "ADEQUADA" : "ENVIADA";
  const [dataEnvioComprovante, setDataEnvioComprovante] = useState<string | null>(null);

  const handleConfirmarEnvio = async () => {
    if (solicitacaoId == null || solicitacaoId <= 0) {
      setToast({
        show: true,
        type: "error",
        message: "Solicitação inválida. Recarregue a página e tente novamente.",
      });
      return;
    }

    setSending(true);
    try {
      flushSync(() => {
        setDataEnvioComprovante(new Date().toISOString());
      });
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));

      const source = pdfSourceRef.current;
      if (!source) {
        throw new Error("Comprovante para PDF não está disponível.");
      }

      const pdfBlob = await gerarPdfBlobComprovanteEnvioV2(source, solicitacaoId);

      if (isAdequacao) {
        await postSolicitacaoAdequacao(solicitacaoId, pdfBlob);
      } else {
        await enviarSolicitacao({
          solicitacaoId,
          PdfResumo: pdfBlob,
        });
      }
      onHide();
      setToast({
        show: true,
        type: "success",
        message: isAdequacao
          ? "Adequação registrada com sucesso."
          : "Documentação enviada com sucesso!",
      });
      window.setTimeout(() => onEnvioSuccess?.(), 900);
    } catch {
      setToast({
        show: true,
        type: "error",
        message: isAdequacao
          ? "Não foi possível registrar a adequação. Tente novamente."
          : "Não foi possível concluir o envio. Tente novamente.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {show && solicitacaoId != null ? (
        <div
          className="confirmacao-envio-v2-pdf-source"
          aria-hidden="true"
          data-testid="confirmacao-envio-v2-pdf-source"
        >
          <ComprovanteEnvioDocumentacaoV2Corpo
            containerRef={pdfSourceRef}
            dadosSindicato={dadosSindicato}
            secoesObrigatorios={secoesObrigatoriosResumo}
            secoesBonus={secoesBonusResumo}
            secoesBonusExtra={secoesBonusExtraResumo}
            enviadoEm={dataEnvioComprovante}
            tituloLinha2={tituloLinha2Comprovante}
            paraExportacaoPdf
          />
        </div>
      ) : null}

      <Modal
        show={show}
        onHide={() => {
          if (sending) return;
          onHide();
        }}
        backdrop={sending ? "static" : true}
        keyboard={!sending}
        size="lg"
        centered
        scrollable
        contentClassName="shipping-doc-v2-font confirmacao-envio-v2-modal-content"
      >
        <Modal.Header closeButton={!sending}>
          <Modal.Title>
            {isAdequacao
              ? "Confirmação da adequação da documentação"
              : "Confirmação do envio da documentação"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="confirmacao-envio-v2-body">
          <p className="text-muted small">
            {isAdequacao
              ? "Revise o resumo abaixo. Ao confirmar, o sistema registrará a adequação enviada ao analista (correções já salvas)."
              : "Revise os dados abaixo (refletem o que foi salvo no sistema). O envio final usa apenas informações já persistidas. Em seguida, confirme para concluir."}
          </p>

          {!isAdequacao && dadosSindicato && (
            <section className="confirmacao-envio-v2-section">
              <h6 className="confirmacao-envio-v2-section-title">Cadastro / sindicato</h6>
              <ul className="confirmacao-envio-v2-list small">
                {(dadosSindicato.nomeSindicato || dadosSindicato.nome) && (
                  <li>
                    <strong>Nome:</strong>{" "}
                    {dadosSindicato.nomeSindicato ?? dadosSindicato.nome}
                  </li>
                )}
                {dadosSindicato.cnpj && (
                  <li>
                    <strong>CNPJ:</strong> {dadosSindicato.cnpj}
                  </li>
                )}
                {dadosSindicato.responsavelPreenchimento && (
                  <li>
                    <strong>Responsável:</strong> {dadosSindicato.responsavelPreenchimento}
                    {dadosSindicato.cargoResponsavel
                      ? ` — ${dadosSindicato.cargoResponsavel}`
                      : ""}
                  </li>
                )}
              </ul>
            </section>
          )}

          {blocos.map((b) => (
            <section key={b.titulo} className="confirmacao-envio-v2-section">
              <h6 className="confirmacao-envio-v2-section-title">{b.titulo}</h6>
              <ul className="confirmacao-envio-v2-list small">
                {b.linhas.map((l, i) =>
                  l.kind === "perguntaResposta" ? (
                    <li key={`${b.titulo}-${i}`} className="confirmacao-envio-v2-pergunta-resposta">
                      <div className="confirmacao-envio-v2-pergunta">{l.pergunta}</div>
                      <div className="confirmacao-envio-v2-resposta-linha">
                        <strong>Resposta:</strong> {l.resposta}
                      </div>
                    </li>
                  ) : (
                    <li key={`${b.titulo}-${i}`}>{l.text}</li>
                  ),
                )}
              </ul>
            </section>
          ))}
        </Modal.Body>
        <Modal.Footer className="border-0 merit-confirm-footer">
          <Button
            type="button"
            variant="outline-secondary"
            className="merit-confirm-btn-nao"
            onClick={onHide}
            disabled={sending}
          >
            Não
          </Button>
          <Button
            type="button"
            variant="primary"
            className="merit-confirm-btn-sim"
            onClick={() => void handleConfirmarEnvio()}
            disabled={sending}
          >
            Sim
          </Button>
        </Modal.Footer>
        {sending ? (
          <div
            className="confirmacao-envio-v2-loading-overlay"
            aria-busy="true"
            aria-live="polite"
          >
            <Spinner
              animation="border"
              variant="primary"
              className="page-loading__spinner"
            />
          </div>
        ) : null}
      </Modal>

      <NotificationToast
        show={toast.show}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
        message={toast.message}
        type={toast.type}
      />
    </>
  );
};

export default ConfirmacaoEnvioV2Modal;
